import React, { useState, useEffect, useRef } from 'react';
import './LocationPicker.css';

const LocationPicker = ({ 
  value = { address: '', latitude: null, longitude: null }, 
  onChange, 
  required = false,
  error = null 
}) => {
  const [address, setAddress] = useState(value.address || '');
  const [coordinates, setCoordinates] = useState({
    latitude: value.latitude,
    longitude: value.longitude
  });
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [showMap, setShowMap] = useState(!value.latitude && !value.longitude);
  const [countryCode, setCountryCode] = useState(null);
  const [addressComponents, setAddressComponents] = useState({ street: '', area: '', city: '', postcode: '' });
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const suggestionContainerRef = useRef(null);

  const reverseGeocode = async (lat, lon) => {
    try {
      const r1 = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&addressdetails=1&accept-language=en`);
      const d1 = await r1.json();
      if (d1 && d1.address) {
        const a = d1.address;
        const street = [a.house_number, a.road].filter(Boolean).join(' ');
        const area = a.neighbourhood || a.suburb || a.hamlet || '';
        const city = a.city || a.town || a.village || a.county || '';
        const postcode = a.postcode || '';
        const parts = [street, area, city, a.state, postcode, a.country].filter(Boolean);
        setAddress(parts.join(', '));
        setAddressComponents({ street, area, city, postcode });
        setCountryCode(a.country_code ? String(a.country_code).toUpperCase() : null);
        return;
      }
    } catch (_) {}
    try {
      const r2 = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`);
      const d2 = await r2.json();
      if (d2 && d2.display_name) {
        setAddress(d2.display_name);
      } else {
        const p = [d2.locality, d2.principalSubdivision, d2.countryName].filter(Boolean);
        setAddress(p.join(', ') || `${lat.toFixed(6)}, ${lon.toFixed(6)}`);
      }
      const area = d2.locality || '';
      const city = d2.city || d2.locality || '';
      const postcode = d2.postcode || '';
      setAddressComponents({ street: '', area, city, postcode });
      setCountryCode((d2.countryCode || d2.country_code || '').toString().toUpperCase() || null);
    } catch (_) {
      setAddress(`${lat.toFixed(6)}, ${lon.toFixed(6)}`);
    }
  };

  useEffect(() => {
    // Update parent component when location changes
    if (onChange) {
      onChange({
        address,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        countryCode,
        components: addressComponents
      });
    }
  }, [address, coordinates.latitude, coordinates.longitude, countryCode, addressComponents, onChange]);

  // Get user's approximate location for search biasing
  useEffect(() => {
    if (navigator.geolocation && !userLocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          // Silently fail - we'll use IP-based location as fallback
          console.log('Could not get user location for search biasing:', error.message);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000 // 5 minutes cache
        }
      );
    }
  }, [userLocation]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser.');
      return;
    }

    setIsGettingLocation(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCoordinates({ latitude, longitude });
        await reverseGeocode(latitude, longitude);
        setIsGettingLocation(false);
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location access denied by user.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information is unavailable.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out.');
            break;
          default:
            setLocationError('An unknown error occurred while getting location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const searchSuggestions = async (query) => {
    if (!query.trim() || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    try {
      let searchUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=8&addressdetails=1`;
      
      // Add location bias if user location is available
      if (userLocation) {
        searchUrl += `&viewbox=${userLocation.longitude - 1},${userLocation.latitude + 1},${userLocation.longitude + 1},${userLocation.latitude - 1}&bounded=0`;
      }
      
      const response = await fetch(searchUrl);
      const data = await response.json();
      
      if (data && data.length > 0) {
        let formattedSuggestions = data.map(item => ({
          display_name: item.display_name,
          latitude: parseFloat(item.lat),
          longitude: parseFloat(item.lon),
          address: item.display_name,
          countryCode: item?.address?.country_code ? String(item.address.country_code).toUpperCase() : null,
          importance: item.importance || 0,
          distance: userLocation ? calculateDistance(
            userLocation.latitude, 
            userLocation.longitude, 
            parseFloat(item.lat), 
            parseFloat(item.lon)
          ) : null
        }));

        // Sort by proximity if user location is available, otherwise by importance
        if (userLocation) {
          formattedSuggestions.sort((a, b) => {
            // Prioritize results within 100km, then by distance
            const aIsNear = a.distance < 100;
            const bIsNear = b.distance < 100;
            
            if (aIsNear && !bIsNear) return -1;
            if (!aIsNear && bIsNear) return 1;
            if (aIsNear && bIsNear) return a.distance - b.distance;
            
            // For distant results, sort by importance
            return (b.importance || 0) - (a.importance || 0);
          });
        } else {
          // Fallback to importance-based sorting
          formattedSuggestions.sort((a, b) => (b.importance || 0) - (a.importance || 0));
        }

        // Limit to 5 suggestions
        setSuggestions(formattedSuggestions.slice(0, 5));
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setIsSearching(false);
  };

  // Calculate distance between two points in kilometers
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleAddressChange = (e) => {
    const newAddress = e.target.value;
    setAddress(newAddress);
    
    // Clear coordinates when manually editing address
    if (coordinates.latitude || coordinates.longitude) {
      setCoordinates({ latitude: null, longitude: null });
    }

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search suggestions
    searchTimeoutRef.current = setTimeout(() => {
      searchSuggestions(newAddress);
    }, 300); // 300ms delay for debouncing
  };

  const handleSuggestionClick = (suggestion) => {
    setAddress(suggestion.address);
    setCoordinates({
      latitude: suggestion.latitude,
      longitude: suggestion.longitude
    });
    if (suggestion.countryCode) {
      setCountryCode(suggestion.countryCode);
    }
    setShowSuggestions(false);
    setSuggestions([]);
    setLocationError('');
    reverseGeocode(suggestion.latitude, suggestion.longitude);
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const searchAddress = async () => {
    if (!address.trim()) return;

    try {
      // Using a free geocoding service
      const response = await fetch(
        `https://api.bigdatacloud.net/data/geocode-forward?query=${encodeURIComponent(address)}&key=free`
      );
      const data = await response.json();
      
      if (data && data.results && data.results.length > 0) {
        const result = data.results[0];
        setCoordinates({
          latitude: result.latitude,
          longitude: result.longitude
        });
        setAddress(result.display_name || address);
        if (result.countryCode || result.country_code) {
          setCountryCode(String(result.countryCode || result.country_code).toUpperCase());
        }
        setLocationError('');
      } else {
        setLocationError('Address not found. Please try a different address.');
      }
    } catch (error) {
      console.error('Error searching address:', error);
      setLocationError('Error searching for address. Please try again.');
    }
  };

  const loadLeafletAssets = () => {
    return new Promise((resolve, reject) => {
      if (window.L) {
        resolve();
        return;
      }
      const cssId = 'leaflet-css';
      if (!document.getElementById(cssId)) {
        const link = document.createElement('link');
        link.id = cssId;
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      const scriptId = 'leaflet-js';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      } else {
        resolve();
      }
    });
  };

  useEffect(() => {
    const initMap = async () => {
      if (!showMap) return;
      try {
        await loadLeafletAssets();
        const L = window.L;
        if (!mapContainerRef.current) return;
        const centerLat = coordinates.latitude || userLocation?.latitude || 20;
        const centerLon = coordinates.longitude || userLocation?.longitude || 0;
        if (!mapRef.current) {
          mapRef.current = L.map(mapContainerRef.current).setView([centerLat, centerLon], 13);
          const providers = [
            { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OpenStreetMap contributors' },
            { url: 'https://{s}.tile.openstreetmap.de/{z}/{x}/{y}.png', attribution: '© OpenStreetMap contributors' },
            { url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', attribution: '© OpenStreetMap contributors' }
          ];
          let idx = 0;
          let layer;
          const tryProvider = () => {
            const p = providers[idx];
            layer = L.tileLayer(p.url, { attribution: p.attribution, crossOrigin: true });
            layer.on('tileerror', () => {
              if (idx < providers.length - 1) {
                mapRef.current.removeLayer(layer);
                idx += 1;
                tryProvider();
              } else {
                setLocationError('Map tiles failed to load. Please check your network.');
              }
            });
            layer.addTo(mapRef.current);
          };
          tryProvider();
          setTimeout(() => {
            mapRef.current.invalidateSize(true);
          }, 0);
        } else {
          mapRef.current.setView([centerLat, centerLon], 13);
          setTimeout(() => {
            mapRef.current.invalidateSize(true);
          }, 0);
        }
        let marker;
        if (coordinates.latitude && coordinates.longitude) {
          marker = L.marker([coordinates.latitude, coordinates.longitude]).addTo(mapRef.current);
        } else if (userLocation?.latitude && userLocation?.longitude) {
          const lat = userLocation.latitude;
          const lon = userLocation.longitude;
          marker = L.marker([lat, lon]).addTo(mapRef.current);
          setCoordinates({ latitude: lat, longitude: lon });
          await reverseGeocode(lat, lon);
          setLocationError('');
        }
        mapRef.current.on('click', async (e) => {
          const { lat, lng } = e.latlng;
          setCoordinates({ latitude: lat, longitude: lng });
          if (marker) {
            marker.setLatLng([lat, lng]);
          } else {
            marker = L.marker([lat, lng]).addTo(mapRef.current);
          }
          await reverseGeocode(lat, lng);
          setLocationError('');
        });
      } catch (err) {
        setLocationError('Map failed to load. Please try again later.');
      }
    };
    initMap();
  }, [showMap, userLocation, coordinates.latitude, coordinates.longitude]);

  return (
    <div className="location-picker">
      <label className="location-label">
        Restaurant Location {required && <span className="required">*</span>}
      </label>
      
      <div className="location-input-group">
        <div className="input-container">
          <input
            type="text"
            className={`location-input ${error ? 'error' : ''}`}
            placeholder="Enter restaurant address..."
            value={address}
            onChange={handleAddressChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            required={required}
          />
          {isSearching && (
            <div className="search-spinner">
              <span className="spinner small"></span>
            </div>
          )}
          
          {showSuggestions && suggestions.length > 0 && (
            <div className="suggestions-dropdown" ref={suggestionContainerRef}>
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className="suggestion-text">
                    📍 {suggestion.display_name}
                    {suggestion.distance && suggestion.distance < 100 && (
                      <span className="distance-indicator">
                        {suggestion.distance < 1 
                          ? `${Math.round(suggestion.distance * 1000)}m away`
                          : `${suggestion.distance.toFixed(1)}km away`
                        }
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <button
          type="button"
          className="search-address-btn"
          onClick={searchAddress}
          disabled={!address.trim()}
        >
          Search
        </button>
      </div>

      <div className="location-actions">
        <button
          type="button"
          className="get-location-btn"
          onClick={getCurrentLocation}
          disabled={isGettingLocation}
        >
          {isGettingLocation ? (
            <>
              <span className="spinner"></span>
              Getting Location...
            </>
          ) : (
            <>
              📍 Use Current Location
            </>
          )}
        </button>
        <button
          type="button"
          className="map-toggle-btn"
          onClick={() => setShowMap((s) => !s)}
        >
          {showMap ? 'Hide Map' : 'Pick from Map'}
        </button>
      </div>

      {address && (
        <div className="selected-address-display">
          <small>📍 Selected address: {address}</small>
        </div>
      )}

      {showMap && (
        <div ref={mapContainerRef} className="map-container"></div>
      )}

      {(error || locationError) && (
        <div className="location-error">
          {error || locationError}
        </div>
      )}

      <div className="location-help">
        <small>
          Enter your restaurant's full address or use current location. This helps customers find you easily.
        </small>
      </div>
    </div>
  );
};

export default LocationPicker;
